import { useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useClientAuth } from '../hooks/useClientAuth';
import { Bell, CircleHelp, LogOut, MessageSquare, Menu } from 'lucide-react';
import { Button, Modal } from '@babomrs/ui';
import { ClientHelpPanel } from '../components/ClientHelp';
import { EntitySwitcher } from './EntitySwitcher';
import { useFeedback } from '@babomrs/ui';
import { hasUnsavedAnswer } from '../lib/unsavedAnswer';
import { useNotifications } from '../features/notifications/useNotifications';
import { NOTIFICATIONS_PATH } from './ClientPortalNav';

/**
 * Client portal topbar — navigation hamburger (below `md`), firm logo (links to portal home), the entity switcher,
 * contextual Help, Report A Problem, the person's name and Sign Out (cp-shell, Story #26234).
 *
 * Responsive behaviour follows `.claude/reference/frontend-ui-system.md` § Responsive breakpoints and § Mobile
 * navigation pattern (both binding): below `md` the sidebar is off-canvas and is opened from here; at `md` and above
 * the rail is visible inline, so the hamburger is not rendered.
 *
 * Every control is a >=44x44 target with >=8px between targets (BR-CP-HOME-003, § Touch-target sizing, binding).
 * With every control at 44px a 375px bar has no room for the switcher, so below `sm` (640px) the switcher takes its
 * own row under the bar. The environment badge is NOT here: it sits at the top of the navigation (BR-CP-HOME-004).
 *
 * The notification bell (BR-CP-HOME-010, BR-CP-HOME-040; Story #26236) opens Notifications. Its count is the person's
 * UNREAD notifications, hidden at 0 — never signatures + queries added together (defects-register #135). It is the one
 * count in the portal that falls when the client looks rather than when they act (BR-CP-HOME-044).
 */
interface ClientTopbarProps {
  /** Drawer state, for `aria-expanded`. */
  navOpen?: boolean;
  /** Toggles the off-canvas nav. Omit and no hamburger renders. */
  onToggleNav?: () => void;
  /** The shell holds this so it can restore focus here when the drawer closes. */
  hamburgerRef?: RefObject<HTMLButtonElement>;
  /**
   * BR-CP-HOME-017 — the Microsoft (MSAL) sign-out, the SAME function the inactivity sign-out calls: it tells every
   * other open tab, ends the identity-provider session and with it any company-register SMS step-up, and lands the
   * client on the sign-in page. Omit and no Sign Out renders.
   */
  onSignOut?: () => void;
}

/** 44x44 target (h-11 / min-w-11 = 2.75rem) for every chrome control (BR-CP-HOME-003). */
const CONTROL =
  // shrink-0 + whitespace-nowrap: the prototype's .tbtn is flex:0 0 auto, so a label never wraps onto two lines.
  'h-11 min-w-11 px-2.5 flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 rounded-md text-[var(--text3)] hover:text-[var(--text1)] hover:bg-[var(--surface2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]';

export function ClientTopbar({ navOpen = false, onToggleNav, hamburgerRef, onSignOut }: ClientTopbarProps) {
  const { user } = useClientAuth();
  const { openFeedback } = useFeedback();
  const [helpOpen, setHelpOpen] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const notifications = useNotifications();
  const unread = notifications.data?.unreadCount ?? 0;

  // No confirmation — except when an answer to a query is typed and not sent (DL-X-30 D18).
  const requestSignOut = () => {
    if (!onSignOut) return;
    if (hasUnsavedAnswer()) {
      setUnsavedOpen(true);
      return;
    }
    onSignOut();
  };

  return (
    <header className="bg-[var(--surface)] border-b border-[var(--border)] sticky top-0 z-10">
      {/* One row from 640px; below it the switcher wraps onto its own row (order-last + basis-full). ONE switcher,
          moved by layout, never two copies — two would mean two comboboxes and duplicate ids. */}
      <div className="min-h-14 sm:py-1.5 flex flex-wrap sm:flex-nowrap items-center gap-x-2 px-4">
        <div className="flex shrink-0 items-center gap-2">
          {/* Hamburger — below `md` only, where the sidebar is off-canvas. At `md` and
              above the icon rail is inline, so there is nothing to open. */}
          {onToggleNav && (
            <button
              ref={hamburgerRef}
              type="button"
              onClick={onToggleNav}
              className="md:hidden w-11 h-11 -ml-2 flex items-center justify-center rounded-md text-[var(--text3)] hover:text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              aria-label="Open navigation"
              aria-expanded={navOpen}
              aria-controls="portal-nav"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          {/*
            The brand is the way back to the portal home, kept from #16982. It is no
            longer the ONLY way back: ClientPortalNav now carries every destination
            (#18259), which is the durable fix #16982 recorded as separate work.
          */}
          <Link
            to="/portal"
            className="h-11 flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
            aria-label="Go to portal home"
          >
            <div className="w-8 h-8 rounded-full bg-[var(--accent)] text-white flex items-center justify-center text-xs font-medium">
              BA
            </div>
            <span className="text-sm font-semibold text-[var(--text1)] hidden sm:inline">B&amp;A Accounting</span>
          </Link>
        </div>

        {/* Entity switcher (spec §7) — between the brand and the spacer, as the prototype's CTOP places it.
            Fills the slot #18259 reserved for `_project/client-portal-enhancements.md` item U. From `sm` up it
            shares the bar; below `sm` the row under the bar carries it (BR-CP-HOME-003). */}
        {user && (
          <div data-testid="switcher-slot" className="order-last basis-full pb-1.5 flex min-w-0 sm:order-none sm:basis-auto sm:shrink sm:pb-0">
            <EntitySwitcher />
          </div>
        )}

        <div className="flex-1" />

        {user && (
          <div className="flex items-center gap-2">
            {/* Help — contextual guidance for the current screen. frontend-ui-system.md
                § In-app help system is binding: the button must be visible on every
                screen, in a consistent position. Order matches the portal prototype. */}
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className={CONTROL}
              aria-label="Help for this page"
            >
              <CircleHelp className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Help</span>
            </button>
            {/* Report A Problem — bugs only (BR-CP-HOME-012). Renamed from "Feedback" so a client does not take it
                for a way to reach their accountant; questions go to Queries. */}
            <button
              type="button"
              onClick={() => openFeedback()}
              className={CONTROL}
              aria-label="Report A Problem"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">Report A Problem</span>
            </button>
            {/* The bell — after Report A Problem, as #cp-shell orders it (#27999). BR-CP-HOME-040: opens Notifications; the count is unread items, hidden at 0. */}
            <Link to={NOTIFICATIONS_PATH} className={`${CONTROL} relative`} aria-label={`Notifications, ${unread} unread`}>
              <Bell className="w-5 h-5" aria-hidden="true" />
              {unread > 0 && (
                <span
                  aria-hidden="true"
                  data-testid="bell-count"
                  // The prototype's `.tbtn .cnt` (#cp-shell, Story #27999): a 16px red count at the top right of the bell.
                  className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-[var(--err)] text-white text-[10px] font-bold leading-4 text-center"
                >
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Link>
            {/* Hidden below lg, with Sign Out, so the switcher keeps a usable width at tablet sizes (DL-CP-55). */}
            <span className="hidden lg:inline text-sm text-[var(--text2)] truncate max-w-[12rem]">{user.displayName}</span>
            {onSignOut && (
              // BR-CP-HOME-017 — icon and label from 1024px; icon only below, named "Sign Out".
              <button type="button" onClick={requestSignOut} className={CONTROL} aria-label="Sign Out">
                <LogOut className="w-5 h-5" />
                <span className="text-sm hidden lg:inline">Sign Out</span>
              </button>
            )}
            <ClientHelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
          </div>
        )}
      </div>

      {/* DL-X-30 D18 — Sign Out while an answer is typed and not sent. Keep Editing is the default and Escape does
          the same; Sign Out Anyway signs out exactly as Sign Out does. */}
      <Modal
        open={unsavedOpen}
        onClose={() => setUnsavedOpen(false)}
        title="You have an unsaved answer"
        size="sm"
        role="alertdialog"
        initialFocusRef={keepEditingRef}
        describedById="unsaved-answer-desc"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setUnsavedOpen(false);
                onSignOut?.();
              }}
            >
              Sign Out Anyway
            </Button>
            <Button ref={keepEditingRef} variant="primary" onClick={() => setUnsavedOpen(false)}>
              Keep Editing
            </Button>
          </>
        }
      >
        <p id="unsaved-answer-desc" className="text-sm text-[var(--text2)]">
          Your answer to a question from your accountant has not been sent. If you sign out now, it will be lost.
        </p>
      </Modal>
    </header>
  );
}
